import React from 'react';
import { Building2, CreditCard, FileText, Search } from 'lucide-react';
import { Company } from '@/types';

interface BookingTypePanelProps {
  isCorporate: boolean;
  onIsCorporateChange: (value: boolean) => void;
  paymentMethod: 'card' | 'invoice';
  onPaymentMethodChange: (method: 'card' | 'invoice') => void;
  companySearch: string;
  onCompanySearchChange: (value: string) => void;
  isCompanyDropdownOpen: boolean;
  onCompanyDropdownOpen: () => void;
  onCompanyDropdownClose: () => void;
  filteredCompanies: Company[];
  isCompanySearchLoading: boolean;
  companySearchError: string;
  selectedCompanyId: string;
  selectedCompany: Company | undefined;
  onSelectCompany: (id: string, name: string) => void;
  poNumber: string;
  onPoNumberChange: (value: string) => void;
}

export const BookingTypePanel: React.FC<BookingTypePanelProps> = ({
  isCorporate,
  onIsCorporateChange,
  paymentMethod,
  onPaymentMethodChange,
  companySearch,
  onCompanySearchChange,
  isCompanyDropdownOpen,
  onCompanyDropdownOpen,
  onCompanyDropdownClose,
  filteredCompanies,
  isCompanySearchLoading,
  companySearchError,
  selectedCompanyId,
  selectedCompany,
  onSelectCompany,
  poNumber,
  onPoNumberChange
}) => (
  <div className="pt-6 mt-6 border-t space-y-6">
    <div>
      <label className="block text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
        <Building2 className="w-4 h-4" /> Booking Type
      </label>
      <div className="grid grid-cols-2 gap-4">
        <button
          onClick={() => {
            onIsCorporateChange(false);
            onPaymentMethodChange('card');
          }}
          className={`p-4 border-2 rounded-xl text-center transition-all ${!isCorporate ? 'border-teal-500 bg-teal-50 text-teal-700 font-bold' : 'border-slate-200 text-slate-500'}`}
        >
          Private Booking
        </button>
        <button
          onClick={() => {
            onIsCorporateChange(true);
            onPaymentMethodChange('invoice');
          }}
          className={`p-4 border-2 rounded-xl text-center transition-all ${isCorporate ? 'border-blue-500 bg-blue-50 text-blue-700 font-bold' : 'border-slate-200 text-slate-500'}`}
        >
          Corporate Booking
        </button>
      </div>
    </div>

    <div>
      <label className="block text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
        <CreditCard className="w-4 h-4" /> Payment Method
      </label>
      <div className="grid grid-cols-2 gap-4">
        <button
          onClick={() => onPaymentMethodChange('card')}
          className={`p-3 border-2 rounded-xl text-sm transition-all flex items-center justify-center gap-2 ${paymentMethod === 'card' ? 'border-purple-500 bg-purple-50 text-purple-700 font-bold' : 'border-slate-100 text-slate-500'}`}
        >
          <CreditCard className="w-4 h-4" /> Credit / Debit Card
        </button>
        <button
          onClick={() => onPaymentMethodChange('invoice')}
          className={`p-3 border-2 rounded-xl text-sm transition-all flex items-center justify-center gap-2 ${paymentMethod === 'invoice' ? 'border-purple-500 bg-purple-50 text-purple-700 font-bold' : 'border-slate-100 text-slate-500'}`}
        >
          <FileText className="w-4 h-4" /> Raise Invoice
        </button>
      </div>
    </div>

    {isCorporate && (
      <div className="animate-fade-in grid grid-cols-1 md:grid-cols-2 gap-4 bg-blue-50/50 p-4 rounded-2xl border border-blue-100">
        <div>
          <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Company</label>
          <div className="relative">
            <Search className="absolute left-3 top-3 text-slate-400 w-4 h-4" />
            <input
              type="text"
              className="w-full pl-9 pr-3 py-2.5 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm"
              placeholder="Search and select company..."
              value={companySearch}
              onFocus={onCompanyDropdownOpen}
              onBlur={onCompanyDropdownClose}
              onChange={(e) => {
                const value = e.target.value;
                onCompanySearchChange(value);
                onCompanyDropdownOpen();
                if (!value.trim()) {
                  onSelectCompany('', '');
                } else if (selectedCompanyId && selectedCompany) {
                  const normalized = value.trim().toLowerCase();
                  if (normalized !== selectedCompany.name.toLowerCase()) {
                    onSelectCompany('', '');
                  }
                }
              }}
            />
            {isCompanyDropdownOpen && (
              <div className="absolute top-full left-0 w-full bg-white border rounded-xl shadow-xl mt-1 z-30 overflow-hidden divide-y max-h-56 overflow-y-auto">
                {isCompanySearchLoading ? (
                  <div className="p-3 text-sm text-slate-500">Searching companies...</div>
                ) : companySearchError ? (
                  <div className="p-3 text-sm text-red-500">{companySearchError}</div>
                ) : filteredCompanies.length === 0 ? (
                  <div className="p-3 text-sm text-slate-400">No matching companies found.</div>
                ) : (
                  filteredCompanies.slice(0, 15).map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onMouseDown={() => onSelectCompany(c.id, c.name)}
                      className="w-full p-3 text-left hover:bg-blue-50 flex items-center justify-between transition-colors"
                    >
                      <div>
                        <div className="font-bold text-sm text-slate-800">
                          {c.name}
                          {c.status === 'on_hold' ? ' (On Hold)' : ''}
                        </div>
                        <div className="text-xs text-slate-500">{c.email}</div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
        <div>
          <label className="block text-xs font-bold text-slate-500 uppercase mb-1 flex items-center gap-1">
            <FileText className="w-3 h-3" />
            {selectedCompany?.requirePo ? 'PO Number (Required)' : 'PO Number (Optional)'}
          </label>
          <input
            placeholder={
              selectedCompany?.requirePo ? 'Required PO Reference' : 'Optional Reference'
            }
            className={`w-full border rounded-lg p-2.5 text-sm transition-all ${selectedCompany?.requirePo && !poNumber ? 'bg-red-50 border-red-200' : 'bg-white border-slate-200'}`}
            value={poNumber}
            onChange={(e) => onPoNumberChange(e.target.value)}
          />
        </div>
      </div>
    )}
  </div>
);
